import { inject, Injectable } from '@angular/core';
import { from, Subject } from 'rxjs';
import { catchError, concatMap, takeUntil, tap } from 'rxjs/operators';
import { OauthAEMConfig } from '../../models/login';
import { UserDataService } from '../login/data-services/user-session/user-data/user-data.service';
import { UserSessionService } from '../login/data-services/user-session/user-session.service';
import { CookieHelperService, LoggingService } from '../utils';

@Injectable({
  providedIn: 'root'
})
export class LogoutService {
  private userData = inject(UserDataService);
  private userSession = inject(UserSessionService);
  private cookieHelper = inject(CookieHelperService);
  private logging = inject(LoggingService);
  private unsubscribe$ = new Subject();

  logOut(differentRedirectUrl?: string, redirectToCurrentUrl?: boolean): void {
    this.userData
      .getAuthConfigContent()
      .pipe(
        concatMap((config: OauthAEMConfig) =>
          this.userData.bffLogout(config).pipe(
            takeUntil(this.unsubscribe$),
            tap(() => {
              this.removeCookieDuringLogout(config);
              this.pingLogOut(
                config,
                redirectToCurrentUrl,
                differentRedirectUrl
              );
            }),
            catchError((error) => {
              this.logging.log(error);
              window.location.replace('/');
              return from([]);
            })
          )
        ),
        catchError((error) => {
          this.logging.log(error);
          window.location.replace('/');
          return from([]);
        })
      )
      .subscribe();
  }

  pingLogOut(
    config: OauthAEMConfig,
    redirectToCurrentUrl?: boolean,
    differentRedirectUrl?: string
  ): void {
    let pingLogoutUrl = '';

    localStorage.removeItem('encyToken');
    localStorage.removeItem('uid');

    if (this.configCheck(config)) {
      pingLogoutUrl = `${config.ISSUER_PROD}${config.LOG_OUT_URL}`;
    } else {
      window.location.replace('/');
    }

    if (redirectToCurrentUrl) {
      window.location.replace(`${pingLogoutUrl}${window.location.href}`);
    } else if (this.differentRedirectUrlCheck(differentRedirectUrl)) {
      window.location.replace(`${pingLogoutUrl}${differentRedirectUrl}`);
    } else {
      window.location.replace(`${pingLogoutUrl}${window.location.origin}`);
    }
  }

  removeCookieDuringLogout(config: OauthAEMConfig): void {
    this.cookieHelper.deleteCookie('isHipmunk');
    this.cookieHelper.deleteCookie('account');
    this.cookieHelper.deleteCookie('accountType');
    this.cookieHelper.deleteCookie('restrictToPersonal');

    if (this.cookieHelper.getCookieValue('prompt')) {
      this.cookieHelper.deleteCookie('prompt');
    }

    if (config.ENABLE_NEW_PERSISTENT_LOGIN) {
      this.userSession.deleteValuesFromServiceWorker();
      this.userSession.deleteStatusValuesFromServiceWorker();
      this.userSession.deleteHeaderValuesFromServiceWorker();
    }
  }

  configCheck(config: OauthAEMConfig): boolean {
    return !!(
      config.ISSUER_PROD &&
      config.LOG_OUT_URL &&
      config.ENABLE_PING_LOGOUT
    );
  }

  differentRedirectUrlCheck(differentRedirectUrl?: string): boolean {
    return !!(
      differentRedirectUrl &&
      differentRedirectUrl.startsWith(window.location.origin)
    );
  }
}
